import React, { useState, useCallback, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { Box, Flex, Button, Icon, TextInput, Text } from "orcs-design-system";
import { themeGet } from "@styled-system/theme-get";

const ActionButton = styled(Button)`
  background: ${({ isActive }) =>
    isActive ? themeGet("colors.greyLighter") : "transparent"};
  border: none !important;
  outline: none !important;
  box-shadow: none !important;
  padding: 4px 8px;
  border-radius: 4px;
  cursor: pointer;
  &:hover:not(:disabled) {
    background: ${themeGet("colors.greyLighter")};
    border: none !important;
    box-shadow: none !important;
  }
  &:focus {
    outline: none !important;
    box-shadow: none !important;
    border: none !important;
  }
  &:active {
    outline: none !important;
    box-shadow: none !important;
    border: none !important;
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    background: transparent;
  }
`;

const FeedbackPanel = styled(Box)`
  background: ${themeGet("colors.greyLightest")};
  border: 1px solid ${themeGet("colors.greyLighter")};
  border-radius: 8px;
  padding: 12px;
  margin-top: 8px;
`;

const CloseButton = styled(Button)`
  background: transparent;
  border: none !important;
  outline: none !important;
  box-shadow: none !important;
  padding: 3px;
  cursor: pointer;
  &:hover,
  &:focus,
  &:active {
    opacity: 0.7;
    border: none !important;
    outline: none !important;
    box-shadow: none !important;
    background: transparent;
  }
`;

/**
 * MessageFeedback component for collecting user feedback on AI messages
 * @param {Object} props
 * @param {string} props.messageId - The ID of the message to provide feedback for
 * @param {string} [props.initialRating] - Initial feedback rating ('positive', 'negative', or null)
 * @param {string} [props.initialComment] - Initial feedback comment
 * @param {Function} props.onSubmitFeedback - Callback when feedback is submitted
 */
const MessageFeedback = ({
  messageId,
  initialRating = null,
  initialComment = "",
  onSubmitFeedback,
  disabled = false,
}) => {
  const [feedbackRating, setFeedbackRating] = useState(initialRating);
  const [showCommentInput, setShowCommentInput] = useState(false);
  const [comment, setComment] = useState(initialComment || "");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inputRef = useRef(null);

  // Focus and select input content when panel opens
  useEffect(() => {
    if (showCommentInput && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [showCommentInput]);

  const handleRatingClick = useCallback(
    async (rating) => {
      const ratingChanged = feedbackRating !== rating;

      if (ratingChanged) {
        setFeedbackRating(rating);
      }

      // Always open the input panel and focus/select input
      if (showCommentInput && inputRef.current) {
        // Panel already open, focus and select directly
        inputRef.current.focus();
        inputRef.current.select();
      } else {
        // Panel will open, useEffect will handle focus/select
        setShowCommentInput(true);
      }

      // Submit the rating immediately if it changed
      if (ratingChanged && onSubmitFeedback) {
        try {
          await onSubmitFeedback({
            messageId,
            feedbackRating: rating,
            feedbackComment: comment || null,
          });
        } catch (error) {
          console.error("Failed to submit feedback rating:", error);
        }
      }
    },
    [feedbackRating, showCommentInput, messageId, comment, onSubmitFeedback]
  );

  const handleSubmitComment = useCallback(async () => {
    if (!comment.trim() || isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    try {
      if (onSubmitFeedback) {
        await onSubmitFeedback({
          messageId,
          feedbackRating,
          feedbackComment: comment.trim(),
        });
      }
      setShowCommentInput(false);
    } catch (error) {
      console.error("Failed to submit feedback comment:", error);
    } finally {
      setIsSubmitting(false);
    }
  }, [comment, isSubmitting, messageId, feedbackRating, onSubmitFeedback]);

  const handleCloseCommentInput = useCallback(() => {
    setShowCommentInput(false);
  }, []);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSubmitComment();
      }
    },
    [handleSubmitComment]
  );

  return (
    <Box mt="s">
      {/* Action buttons row */}
      <Flex alignItems="center" justifyContent="flex-end" gap="xs">
        <ActionButton
          variant="ghost"
          iconOnly
          small
          isActive={feedbackRating === "positive"}
          onClick={() => handleRatingClick("positive")}
          ariaLabel="Thumbs up - helpful response"
          disabled={disabled}
        >
          <Icon
            icon={["far", "thumbs-up"]}
            color={feedbackRating === "positive" ? "primary" : "greyDark"}
            size="sm"
          />
        </ActionButton>
        <ActionButton
          variant="ghost"
          iconOnly
          small
          isActive={feedbackRating === "negative"}
          onClick={() => handleRatingClick("negative")}
          ariaLabel="Thumbs down - not helpful"
          disabled={disabled}
        >
          <Icon
            icon={["far", "thumbs-down"]}
            color={feedbackRating === "negative" ? "danger" : "greyDark"}
            size="sm"
          />
        </ActionButton>
      </Flex>

      {/* Comment input panel */}
      {showCommentInput && (
        <FeedbackPanel>
          <Flex justifyContent="space-between" alignItems="flex-start" mb="s">
            <Text fontSize="1" color="greyDarkest">
              Thanks for your feedback. Would you like to leave a comment before
              continuing?
            </Text>
            <CloseButton
              variant="ghost"
              iconOnly
              small
              onClick={handleCloseCommentInput}
              ariaLabel="Close feedback panel"
            >
              <Icon icon={["fas", "times"]} color="greyDark" size="sm" />
            </CloseButton>
          </Flex>
          <Flex alignItems="center" gap="s">
            <Box flex="1">
              <TextInput
                ref={inputRef}
                id={`feedback-comment-${messageId}`}
                placeholder="Enter your comments..."
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                onKeyDown={handleKeyDown}
                fullWidth
              />
            </Box>
            <Button
              variant="primary"
              small
              onClick={handleSubmitComment}
              disabled={!comment.trim() || isSubmitting}
            >
              Send
            </Button>
          </Flex>
        </FeedbackPanel>
      )}
    </Box>
  );
};

MessageFeedback.propTypes = {
  messageId: PropTypes.string.isRequired,
  initialRating: PropTypes.oneOf(["positive", "negative", null]),
  initialComment: PropTypes.string,
  onSubmitFeedback: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

export default MessageFeedback;
